import React, { useEffect, useMemo, useState } from "react";
import {
  ArrowDown, ArrowUp, Eye, EyeOff, FileText, Loader2, Plus, RotateCcw, Save, Trash2,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { confirmAsync } from "@/lib/safeConfirm";
import {
  bulletsToText, defaultInfoBlocks, editableBlocks, hasCustomBlocks, hasLegacyOverrides, textToBullets,
} from "@/lib/infoBlocks";

// Curated trips are paid in full at checkout, which is what the public
// landing passes to the default copy — seed the editor with the same value
// so what the agent reads here is exactly what the client sees.
const CURATED_DEPOSIT_PCT = 100;

const newKey = () => `custom_${Math.random().toString(36).slice(2, 8)}`;

/**
 * "Textos del cliente" tab — edits the Information & Documents blocks that
 * close the public landing (Quote Terms, What's Included…). One textarea
 * per block, one bullet per line, so the agent can paste straight from
 * Travefy. Text stays in English: it's client-facing copy.
 */
export default function ClientTextsTab({ trip, onChange }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [template, setTemplate] = useState(null);
  const [loadingTpl, setLoadingTpl] = useState(true);
  const [savingTpl, setSavingTpl] = useState(false);

  const blocks = useMemo(
    () => editableBlocks(trip.info_documents, CURATED_DEPOSIT_PCT, template),
    [trip.info_documents, template],
  );
  const isCustom = hasCustomBlocks(trip.info_documents);
  const isImported = !isCustom && hasLegacyOverrides(trip.info_documents);

  // Raw textarea text lives here so pressing Enter doesn't get eaten by the
  // bullets round-trip (empty lines are stripped when parsing).
  const [draft, setDraft] = useState({});
  useEffect(() => {
    api.get("/settings/info-documents")
      .then(({ data }) => setTemplate(data))
      .catch(() => setTemplate({ blocks: [] }))
      .finally(() => setLoadingTpl(false));
  }, []);
  useEffect(() => {
    setDraft(Object.fromEntries(blocks.map((b) => [b.key, bulletsToText(b.bullets)])));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingTpl, isCustom]);

  const commit = (next) => onChange({ info_documents: { blocks: next } });

  const patchBlock = (key, patch) =>
    commit(blocks.map((b) => (b.key === key ? { ...b, ...patch } : b)));

  const onText = (key, value) => {
    setDraft((d) => ({ ...d, [key]: value }));
    patchBlock(key, { bullets: textToBullets(value) });
  };

  const move = (idx, dir) => {
    const next = [...blocks];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    commit(next);
  };

  const addBlock = () => {
    const key = newKey();
    setDraft((d) => ({ ...d, [key]: "" }));
    commit([...blocks, { key, title: "New section", bullets: [], hidden: false }]);
  };

  const removeBlock = async (key, title) => {
    if (!(await confirmAsync(`¿Borrar el bloque «${title || key}» de este viaje?`, {
      destructive: true, confirmLabel: "Borrar",
    }))) return;
    commit(blocks.filter((b) => b.key !== key));
  };

  const resetTo = async (source) => {
    const label = source === "template" ? "la plantilla de la agencia" : "el texto por defecto del sistema";
    if (!(await confirmAsync(`¿Descartar los textos de este viaje y volver a ${label}?`, {
      confirmLabel: "Volver",
    }))) return;
    const base = source === "template" && hasCustomBlocks(template)
      ? template.blocks
      : defaultInfoBlocks(CURATED_DEPOSIT_PCT);
    setDraft(Object.fromEntries(base.map((b) => [b.key, bulletsToText(b.bullets)])));
    // Empty object = "inherit": the API serves the template, and if there
    // is none the public page falls back to the code defaults.
    onChange({ info_documents: source === "template" ? {} : { blocks: base } });
    toast.success(`Textos restaurados desde ${label}`);
  };

  const saveAsTemplate = async () => {
    if (!(await confirmAsync(
      "Estos textos pasarán a ser la plantilla de la agencia y se usarán en todos los viajes que no tengan textos propios. ¿Seguir?",
      { confirmLabel: "Guardar plantilla" },
    ))) return;
    setSavingTpl(true);
    try {
      const { data } = await api.put("/settings/info-documents", { blocks });
      setTemplate({ blocks: data.blocks });
      toast.success("Plantilla de la agencia actualizada");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar la plantilla");
    } finally { setSavingTpl(false); }
  };

  if (loadingTpl) {
    return (
      <div className="text-sm text-clay-700 inline-flex items-center gap-2" data-testid="tab-content-texts">
        <Loader2 size={14} className="animate-spin"/> Cargando textos…
      </div>
    );
  }

  return (
    <div className="grid lg:grid-cols-[1.35fr_1fr] gap-8 items-start" data-testid="tab-content-texts">
      <div>
        <div className="border border-clay-300 bg-white px-4 py-3 mb-5 flex items-start gap-3" data-testid="texts-origin-banner">
          <FileText size={15} className="text-terracotta mt-0.5 shrink-0"/>
          <div className="text-sm text-clay-700">
            {isCustom ? (
              <>Este viaje tiene <strong>textos propios</strong>. Los cambios sólo afectan a este viaje.</>
            ) : isImported ? (
              <>Textos <strong>importados de Travefy</strong> con este viaje. En cuanto edites algo se guardan como textos propios.</>
            ) : hasCustomBlocks(template) ? (
              <>Este viaje usa la <strong>plantilla de la agencia</strong>. En cuanto edites algo, se personaliza sólo para este viaje.</>
            ) : (
              <>Este viaje usa el <strong>texto por defecto del sistema</strong>. En cuanto edites algo, se personaliza sólo para este viaje.</>
            )}
            <div className="text-[11px] mt-1 text-clay-600">
              Una viñeta por línea. El texto es el que ve el cliente, así que va en inglés.
            </div>
          </div>
        </div>

        <div className="space-y-4">
          {blocks.map((b, idx) => (
            <div
              key={b.key}
              className={`border border-clay-300 bg-white ${b.hidden ? "opacity-55" : ""}`}
              data-testid={`text-block-${b.key}`}
            >
              <div className="flex items-center gap-2 px-4 py-3 border-b border-clay-200">
                <input
                  value={b.title || ""}
                  onChange={(e) => patchBlock(b.key, { title: e.target.value })}
                  placeholder="Título del bloque"
                  data-testid={`block-title-${b.key}`}
                  className="flex-1 font-serif text-lg bg-transparent outline-none border-b border-transparent focus:border-terracotta"
                />
                {b.hidden && <span className="text-[10px] uppercase tracking-widest bg-clay-200 text-clay-700 px-2 py-0.5">Oculto</span>}
                <div className="flex items-center gap-0.5 shrink-0">
                  <button onClick={() => move(idx, -1)} disabled={idx === 0} data-testid={`block-up-${b.key}`} className="p-1.5 hover:bg-clay-100 disabled:opacity-25" title="Subir"><ArrowUp size={13}/></button>
                  <button onClick={() => move(idx, 1)} disabled={idx === blocks.length - 1} data-testid={`block-down-${b.key}`} className="p-1.5 hover:bg-clay-100 disabled:opacity-25" title="Bajar"><ArrowDown size={13}/></button>
                  <button onClick={() => patchBlock(b.key, { hidden: !b.hidden })} data-testid={`block-toggle-${b.key}`} className="p-1.5 hover:bg-clay-100" title={b.hidden ? "Mostrar al cliente" : "Ocultar al cliente"}>
                    {b.hidden ? <EyeOff size={13}/> : <Eye size={13}/>}
                  </button>
                  <button onClick={() => removeBlock(b.key, b.title)} data-testid={`block-delete-${b.key}`} className="p-1.5 hover:bg-clay-100 text-destructive" title="Borrar bloque"><Trash2 size={13}/></button>
                </div>
              </div>
              <textarea
                value={draft[b.key] ?? bulletsToText(b.bullets)}
                onChange={(e) => onText(b.key, e.target.value)}
                rows={Math.max(3, (draft[b.key] || "").split("\n").length + 1)}
                placeholder="Una viñeta por línea…"
                data-testid={`block-bullets-${b.key}`}
                className="w-full px-4 py-3 text-[13.5px] leading-relaxed outline-none resize-y font-sans"
              />
              <div className="px-4 pb-3 text-[11px] text-clay-600">
                {(b.bullets || []).length} viñeta{(b.bullets || []).length === 1 ? "" : "s"}
              </div>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-2 mt-5">
          <button onClick={addBlock} data-testid="add-block-btn" className="inline-flex items-center gap-2 px-4 py-2 border border-clay-300 text-sm hover:bg-clay-100">
            <Plus size={14}/> Añadir bloque
          </button>
          {isCustom && (
            <button onClick={() => resetTo("template")} data-testid="reset-template-btn" className="inline-flex items-center gap-2 px-4 py-2 border border-clay-300 text-sm hover:bg-clay-100">
              <RotateCcw size={14}/> Volver a la plantilla
            </button>
          )}
          <button onClick={() => resetTo("defaults")} data-testid="reset-defaults-btn" className="inline-flex items-center gap-2 px-4 py-2 border border-clay-300 text-sm hover:bg-clay-100">
            <RotateCcw size={14}/> Texto por defecto
          </button>
          {isAdmin && (
            <button onClick={saveAsTemplate} disabled={savingTpl} data-testid="save-template-btn" className="inline-flex items-center gap-2 px-4 py-2 bg-clay-900 text-white text-sm tracking-wider uppercase hover:bg-terracotta disabled:opacity-50 ml-auto">
              {savingTpl ? <Loader2 size={14} className="animate-spin"/> : <Save size={14}/>} Guardar como plantilla
            </button>
          )}
        </div>
        {template?.updated_at && (
          <div className="text-[11px] text-clay-600 mt-2">
            Plantilla de la agencia actualizada por {template.updated_by} · {new Date(template.updated_at).toLocaleDateString("es-ES")}
          </div>
        )}
      </div>

      {/* Live preview — same gold-dot bullets as the public landing */}
      <div className="lg:sticky lg:top-4" data-testid="texts-preview">
        <div className="smallcaps mb-3">Así lo verá el cliente</div>
        <div className="border border-clay-300 p-6 space-y-7" style={{ backgroundColor: "#faf7f2" }}>
          {blocks.filter((b) => !b.hidden).length === 0 && (
            <div className="text-sm text-clay-600">No hay bloques visibles: esta sección no aparecerá en la página.</div>
          )}
          {blocks.filter((b) => !b.hidden).map((b) => (
            <div key={b.key}>
              <div className="text-[10px] uppercase tracking-[0.25em] mb-3" style={{ color: "#B08749" }}>{b.title}</div>
              <ul className="text-[13px] space-y-1.5 leading-relaxed text-clay-800">
                {(b.bullets || []).map((line, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="mt-1.5 shrink-0 w-1 h-1 rounded-full" style={{ background: "#B08749" }}/>
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
